/**
 * Pointer / keyboard handling for the game view: camera (drag with inertia, wheel and pinch
 * zoom, WASD/arrows), selection, build placement, aiming, right-click orders and focus.
 * Produces intents through `send` and the overlay the scene draws.
 *
 * Right-click on land pushes the front, on release, exactly as a click always did. Holding the
 * right button still on another nation's land for `MENU_HOLD_MS` instead opens that nation's menu
 * (diplomacy) and sends nothing; a long press does the same on touch.
 */
import { BALANCE, TICK_HZ, buildCost, getGeo, landHexAt, type BuildKind, type EntityId, type EntityView, type Intent, type Vec2 } from '@sa/sim';
import type { LoadedWorld } from '@sa/mapdata';
import type { AimState, GameStore, PlacementState } from '../state/store';
import type { Overlay, SceneOut } from '../render/scene';
import { renderHexAt, roundClass, type HexLookup } from '../render/hexfield';
import { newCityIncome } from '../state/economy';
import { fmtInt } from '../ui/dom';
import { aimClick, computeAim, FF_CONFIRM_MS, FF_CONFIRM_PX, firstImpact, launchConfirm, loadedOf } from './aim';
import type { Camera } from './camera';
import {
  airOrder,
  BUILD_LABEL,
  BUILD_ORDER,
  capOf,
  checkPlacement,
  inSlbmRange,
  isShipKind,
  nearestPort,
  ownedCount,
  ownPorts,
  placementRange,
  readyLaunchers,
  rightClickIntents,
  type PlaceError,
} from './commands';

export type AimAction = 'launch' | 'strike' | 'patrol' | 'scout';

/** `ripple` (launch aim, L pressed again): the click empties the selected magazines */
export type Mode = { type: 'idle' } | { type: 'place'; kind: BuildKind } | { type: 'aim'; action: AimAction; ripple?: boolean };

/** A launcher rippling at a target: it fires again each time it is ready, until its magazine is empty. */
interface Ripple {
  x: number;
  y: number;
  /** loaded count when we last fired (null: not waiting for the server to see that shot) */
  sentLoaded: number | null;
  sentMs: number;
}

/** a ripple shot the server has not shown us within this long is sent again (ms) */
const RIPPLE_RESEND_MS = 2000;

export interface ControllerHooks {
  send(i: Intent): void;
  /** a right-press (long press on touch) was held on another nation's land: open its menu */
  nationHold(owner: number, sx: number, sy: number): void;
  /** a hold on another nation's land started (the ring at the cursor) or ended (null) */
  holdCue(at: { x: number; y: number; ms: number } | null): void;
  /** Esc: close a transient popup if one is open (returns true if it did) */
  dismiss(): boolean;
  /** Esc with nothing to cancel (opens the menu; closes it while it is open) */
  menu(): void;
  /** a modal (the Esc menu) is open: swallow all game input except Esc */
  modal(): boolean;
  /** contextual hotkey for the selection (chip actions) */
  hotkey(key: string): boolean;
  toggleBoard(show: boolean): void;
  /** `order` with a message confirms it on the alert line (e.g. BOMBER OUTBOUND · DROP IN 0:36) */
  feedback(kind: 'place' | 'order' | 'error' | 'warn', msg?: string): void;
}

const DRAG_PX = 5;
const PICK_PX = 13;
/** a click this close (css px) to a hostile missile head, with SAMs selected, makes it their priority */
const MISSILE_PICK_PX = 18;
const KEY_PAN_PX_S = 900;
const WHEEL_STEP = 1.0018;
/**
 * Esc within this long of any order, cancel or deselect (an Esc that cleared a selection, a
 * right-click that dropped a placement) never opens the menu: a reflex "cancel" is not "pause".
 */
export const ESC_GRACE_MS = 1000;
/** placing a sub / carrier snaps to our nearest port within this many css px of the cursor */
const PORT_SNAP_PX = 60;
/** two clicks on our ship this close in time select every ship of that kind on screen */
const DOUBLE_CLICK_MS = 350;
/** the move-order marker plays this long */
export const ORDER_PING_MS = 900;
/**
 * A right-press held still this long on another nation's land opens its menu (diplomacy) instead of
 * pushing. Well above a click (~100 ms), short enough to feel deliberate, not slow.
 */
export const MENU_HOLD_MS = 420;

export const PLACE_ERR: Record<PlaceError, string> = {
  notOwnHex: 'OWN LAND ONLY',
  occupied: 'HEX OCCUPIED',
  radiation: 'IRRADIATED',
  spacing: 'TOO CLOSE TO A CITY',
  notPort: 'NEEDS A COASTAL HEX',
  cap: 'LIMIT REACHED',
  credits: 'NOT ENOUGH CREDITS',
  badHex: 'INVALID SITE',
};

/** the server's focus cooldown, plus a little for the tick the intent waits to be applied */
const FOCUS_COOLDOWN_MS = ((BALANCE.creep.focusCooldownTicks + 2) / TICK_HZ) * 1000;

export class InputController {
  mode: Mode = { type: 'idle' };
  /** last focus sent (ms) and a right-click held until the focus cooldown ends */
  private lastFocusMs = -Infinity;
  private heldFocus: Intent | null = null;
  private sel: EntityId[] = [];
  /** when the selection last gained a unit or was flashed (performance.now ms): its marker pulses in */
  selectedAt = -Infinity;
  private cursor = { sx: -1, sy: -1, inside: false };
  private pointers = new Map<number, { x: number; y: number }>();
  private drag: { x: number; y: number; moved: boolean; lastT: number; vx: number; vy: number; button: number } | null = null;
  private pinch: { d: number } | null = null;
  /** a right-press (or a touch) on another nation's land, waiting to become its menu */
  private hold: { timer: ReturnType<typeof setTimeout>; owner: number } | null = null;
  private keys = new Set<string>();
  lastScene: SceneOut | null = null;
  /** launch aim / placement under the cursor (updated by `overlay()` every frame) */
  aim: AimState | null = null;
  placement: PlacementState | null = null;
  /** friendly-fire warning click: the next click near it, before `untilMs`, fires */
  private ffArm: { sx: number; sy: number; untilMs: number } | null = null;
  /** time of the last order / placement / launch (Esc grace window) */
  private lastActMs = -Infinity;
  /** the last ship move order: where (mu) and when (performance.now ms), for its marker */
  private orderPing: { x: number; y: number; atMs: number; queue: boolean } | null = null;
  /** the last click on one of our units (double-click selects all of that kind on screen) */
  private lastPick: { id: EntityId; atMs: number } | null = null;
  /** launchers rippling at a target (by launcher id) */
  readonly ripples = new Map<EntityId, Ripple>();
  private off: (() => void)[] = [];

  constructor(
    private el: HTMLElement,
    private cam: Camera,
    private store: GameStore,
    private world: LoadedWorld,
    private lookup: HexLookup,
    private hooks: ControllerHooks,
  ) {
    const on = <K extends keyof HTMLElementEventMap>(t: EventTarget, type: K | string, fn: (e: never) => void, opts?: AddEventListenerOptions) => {
      t.addEventListener(type, fn as EventListener, opts);
      this.off.push(() => t.removeEventListener(type, fn as EventListener, opts));
    };
    on(el, 'pointerdown', (e: PointerEvent) => this.down(e));
    on(window, 'pointermove', (e: PointerEvent) => this.move(e));
    on(window, 'pointerup', (e: PointerEvent) => this.up(e));
    on(window, 'pointercancel', (e: PointerEvent) => this.cancelPointer(e));
    on(el, 'wheel', (e: WheelEvent) => this.wheel(e), { passive: false });
    on(el, 'contextmenu', (e: MouseEvent) => e.preventDefault());
    on(el, 'pointerleave', () => (this.cursor.inside = false));
    on(window, 'keydown', (e: KeyboardEvent) => this.keydown(e));
    on(window, 'keyup', (e: KeyboardEvent) => this.keyup(e));
    on(window, 'blur', () => this.keys.clear());
  }

  dispose(): void {
    this.off.forEach((f) => f());
  }

  get selected(): EntityId[] {
    return this.sel;
  }

  /** (a unit that joins the selection makes the marker pulse in; one that leaves does not) */
  set selected(ids: EntityId[]) {
    if (ids.some((id) => !this.sel.includes(id))) this.selectedAt = performance.now();
    // a deselect is a cancel: the Esc grace window starts
    if (this.sel.length && !ids.length) this.acted();
    this.sel = ids;
  }

  /** Pulse the selection marker again (the chip title was pointed at). */
  flashSelection(): void {
    this.selectedAt = performance.now();
  }

  /**
   * The push focus can change once per `creep.focusCooldownTicks` (the server refuses faster
   * changes): a right-click inside that window is held and sent when it ends — the last click wins.
   */
  private sendFocus(i: Intent): void {
    const wait = this.lastFocusMs + FOCUS_COOLDOWN_MS - performance.now();
    if (wait <= 0) {
      this.lastFocusMs = performance.now();
      this.heldFocus = null;
      this.hooks.send(i);
    } else this.heldFocus = i;
  }

  // ---------------------------------------------------------------- per frame

  update(dt: number): void {
    if (this.heldFocus && performance.now() >= this.lastFocusMs + FOCUS_COOLDOWN_MS) this.sendFocus(this.heldFocus);
    if (this.hooks.modal()) {
      this.keys.clear();
      this.drag = null;
      return;
    }
    let dx = 0;
    let dy = 0;
    if (this.keys.has('a') || this.keys.has('arrowleft')) dx += 1;
    if (this.keys.has('d') || this.keys.has('arrowright')) dx -= 1;
    if (this.keys.has('w') || this.keys.has('arrowup')) dy += 1;
    if (this.keys.has('s') || this.keys.has('arrowdown')) dy -= 1;
    if (dx || dy) {
      this.cam.stop();
      this.cam.panBy(dx * KEY_PAN_PX_S * dt, dy * KEY_PAN_PX_S * dt);
    }
    // drop selections that no longer exist
    if (this.selected.length) this.selected = this.selected.filter((id) => this.store.view.entities.has(id));
    this.updateRipples();
  }

  /** Rippling launchers fire again as soon as they are ready, until their magazines are empty. */
  private updateRipples(): void {
    if (!this.ripples.size) return;
    const v = this.store.view;
    const now = performance.now();
    for (const [id, r] of this.ripples) {
      const e = v.entities.get(id);
      if (!e || e.owner !== v.me || loadedOf(e) <= 0 || (e.kind === 'sub' && !inSlbmRange(e, v.tick, r.x, r.y))) {
        this.ripples.delete(id);
        continue;
      }
      // the last shot is not in the view yet: wait for it (or send again if it got lost)
      if (r.sentLoaded !== null && loadedOf(e) >= r.sentLoaded && now - r.sentMs < RIPPLE_RESEND_MS) continue;
      r.sentLoaded = null;
      if (!readyLaunchers(v, [e]).length) continue;
      r.sentLoaded = loadedOf(e);
      r.sentMs = now;
      this.hooks.send({ type: 'launch', launcherId: id, x: r.x, y: r.y });
    }
  }

  /** Stop the ripples of these launchers (all when none given). */
  stopRipple(ids?: readonly EntityId[]): void {
    if (!ids) this.ripples.clear();
    else for (const id of ids) this.ripples.delete(id);
  }

  overlay(): Overlay {
    const now = performance.now();
    const ping = this.orderPing && now - this.orderPing.atMs < ORDER_PING_MS ? this.orderPing : null;
    const ov: Overlay = {
      selected: this.selected,
      selectedAt: this.selectedAt,
      placement: null,
      aim: null,
      ports: null,
      ping: ping && { x: ping.x, y: ping.y, t: (now - ping.atMs) / ORDER_PING_MS, queue: ping.queue },
    };
    this.aim = null;
    this.placement = null;
    if (this.ffArm && now > this.ffArm.untilMs) this.ffArm = null;
    const view = this.store.view;
    // placing a sub / carrier: every port we could build it at is marked
    if (this.mode.type === 'place' && isShipKind(this.mode.kind)) ov.ports = ownPorts(view, this.world).map((p) => ({ x: p.wx, y: p.wy }));
    if (!this.cursor.inside || this.hooks.modal()) return ov;
    const [wx, wy] = this.cam.screenToWorld(this.cursor.sx, this.cursor.sy);
    if (this.mode.type === 'place') {
      const k = this.mode.kind;
      const hex = this.placeHexAt(k, wx, wy);
      const ship = isShipKind(k) && hex >= 0 && this.world.portWater[hex] >= 0;
      // a ship's ghost floats where it will be launched (the port's water), not on the land hex
      const x = hex < 0 ? wx : ship ? this.world.wcx[this.world.portWater[hex]] : this.world.cx[hex];
      const y = hex < 0 ? wy : ship ? this.world.wcy[this.world.portWater[hex]] : this.world.cy[hex];
      const err = checkPlacement(view, this.world, k, hex);
      // a city says what it would earn there (a new city is a slow investment: the caption keeps that honest)
      const earns = k === 'city' && err === null ? ` · +${fmtInt(newCityIncome(view, this.world, hex))}/MIN` : '';
      this.placement = { kind: k, x, y, valid: err === null, reason: err && PLACE_ERR[err], label: `${BUILD_LABEL[k]} · ${fmtInt(buildCost(k))}${earns}`, range: placementRange(k) };
      ov.placement = { kind: k, x, y, valid: err === null, range: placementRange(k), port: ship ? { x: this.world.cx[hex], y: this.world.cy[hex] } : undefined };
    } else if (this.mode.type === 'aim') {
      const sel = this.selection();
      const a = this.mode.action;
      if (a === 'launch') {
        const aim = computeAim(view, this.world, sel, wx, wy, this.armedAt(this.cursor.sx, this.cursor.sy), !!this.mode.ripple);
        this.aim = aim;
        if (aim) {
          const from = sel.filter((e) => e.owner === view.me && (e.kind === 'silo' || e.kind === 'sub')).map((e) => this.posOf(e));
          ov.aim = { kind: aim.kind, from, x: wx, y: wy, valid: aim.ready > 0 && !aim.ownOrAllied };
        }
        this.el.style.cursor = aim?.blocked ? 'not-allowed' : 'crosshair';
      } else {
        const from = sel.filter((e) => e.kind === 'airbase' || e.kind === 'carrier').map((e) => this.posOf(e));
        const r = a === 'strike' ? BALANCE.bomber.radius : BALANCE.fighter.radius;
        const valid = from.some((p) => Math.hypot(p.x - wx, p.y - wy) <= r);
        ov.aim = { kind: 'air', from, x: wx, y: wy, valid };
      }
    }
    return ov;
  }

  /** The world point under the cursor (mu), or null when it is off the map view or a modal is up. */
  hover(): { x: number; y: number } | null {
    if (!this.cursor.inside || this.hooks.modal() || this.drag?.moved) return null;
    const [x, y] = this.cam.screenToWorld(this.cursor.sx, this.cursor.sy);
    return { x, y };
  }

  /** Was a friendly-fire warning click made near (sx, sy), still within its window? */
  private armedAt(sx: number, sy: number): boolean {
    const f = this.ffArm;
    return !!f && performance.now() <= f.untilMs && Math.hypot(f.sx - sx, f.sy - sy) <= FF_CONFIRM_PX;
  }

  // ---------------------------------------------------------------- modes

  setMode(m: Mode): void {
    // leaving a mode (placement, aim, an order's target) is a cancel or an order: the Esc grace window starts
    if (this.mode.type !== 'idle' && m.type === 'idle') this.acted();
    this.mode = m;
    this.ffArm = null;
    this.el.style.cursor = m.type === 'idle' ? '' : 'crosshair';
  }

  /** Spectating (eliminated): the camera is free, nothing else takes orders. */
  private get spectating(): boolean {
    return this.store.view.eliminated;
  }

  /** Arm a build slot (toggle). Unaffordable or capped slots refuse with the reason. */
  startBuild(kind: BuildKind): void {
    if (this.hooks.modal() || this.spectating) return;
    if (this.mode.type === 'place' && this.mode.kind === kind) {
      this.setMode({ type: 'idle' });
      return;
    }
    const v = this.store.view;
    const cost = buildCost(kind);
    if (v.credits < cost) {
      this.hooks.feedback('error', `NOT ENOUGH CREDITS · ${BUILD_LABEL[kind]} ${fmtInt(cost)}`);
      return;
    }
    if (ownedCount(v, kind) >= capOf(kind)) {
      this.hooks.feedback('error', `${BUILD_LABEL[kind]} LIMIT REACHED`);
      return;
    }
    if (isShipKind(kind) && !ownPorts(v, this.world).length) {
      this.hooks.feedback('error', `NO PORT · ${BUILD_LABEL[kind]} NEEDS OUR COAST`);
      return;
    }
    this.setMode({ type: 'place', kind });
  }

  /** Enter an aim mode; Q again while aiming a launch toggles the ripple (empty the magazines). */
  startAim(action: AimAction): void {
    const m = this.mode;
    if (action === 'launch' && m.type === 'aim' && m.action === 'launch') {
      this.mode = { ...m, ripple: !m.ripple };
      return;
    }
    this.setMode({ type: 'aim', action });
  }

  /** Select these launchers and aim a launch with them (Q STRIKE on a rival's critical capital). */
  aimWith(ids: EntityId[]): void {
    this.selected = ids;
    this.setMode({ type: 'aim', action: 'launch' });
  }

  selection(): EntityView[] {
    const v = this.store.view;
    return this.selected.map((id) => v.entities.get(id)).filter((e): e is EntityView => !!e);
  }

  select(ids: EntityId[]): void {
    this.selected = ids;
  }

  private posOf(e: EntityView): Vec2 {
    return this.lastScene?.pos.get(e.id) ?? { x: e.x, y: e.y };
  }

  /** The hex a click at (x, y) would build `kind` on: ships snap to our nearest port. */
  private placeHexAt(kind: BuildKind, x: number, y: number): number {
    if (!isShipKind(kind)) return this.hexAt(x, y);
    const port = nearestPort(this.store.view, this.world, x, y, PORT_SNAP_PX / Math.max(1e-6, this.cam.zoom));
    return port >= 0 ? port : this.hexAt(x, y);
  }

  private hexAt(x: number, y: number): number {
    // the owner classes the territory shader rounds its corners with
    const st = this.store.hexState;
    return renderHexAt(this.world, this.lookup.cells, this.lookup.warp, x, y, (h) => roundClass(st, h, this.store.me));
  }

  /** Own entity under the cursor (screen-space pick on last frame's positions). */
  private pick(sx: number, sy: number): EntityView | null {
    const v = this.store.view;
    let best: EntityView | null = null;
    let bd = PICK_PX * PICK_PX;
    for (const e of v.entities.values()) {
      if (e.owner !== v.me) continue;
      const p = this.posOf(e);
      const [px, py] = this.cam.worldToScreen(p.x, p.y);
      const d = (px - sx) ** 2 + (py - sy) ** 2;
      // parked aircraft sit on their base: prefer the base
      if (d < bd || (d === bd && best && best.kind !== 'airbase' && e.kind === 'airbase')) {
        if ((e.kind === 'fighter' || e.kind === 'bomber') && e.detail?.phase === 'parked') continue;
        bd = d;
        best = e;
      }
    }
    return best;
  }

  // ---------------------------------------------------------------- pointer

  private local(e: PointerEvent | WheelEvent): [number, number] {
    const r = this.el.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  }

  private down(e: PointerEvent): void {
    if (this.hooks.modal()) return;
    const [x, y] = this.local(e);
    this.pointers.set(e.pointerId, { x, y });
    this.el.setPointerCapture?.(e.pointerId);
    if (this.pointers.size === 2) {
      const [a, b] = [...this.pointers.values()];
      this.pinch = { d: Math.hypot(a.x - b.x, a.y - b.y) };
      this.drag = null;
      this.cancelHold();
      return;
    }
    this.cam.stop();
    this.drag = { x, y, moved: false, lastT: performance.now(), vx: 0, vy: 0, button: e.button };
    if (e.button === 2 || (e.pointerType === 'touch' && e.button === 0)) this.startHold(x, y);
  }

  /** The nation whose land is under (sx, sy) when it is another nation's (0: none). */
  private foreignAt(sx: number, sy: number): number {
    const v = this.store.view;
    const [wx, wy] = this.cam.screenToWorld(sx, sy);
    const hex = landHexAt(getGeo(this.world), wx, wy);
    const owner = hex >= 0 ? v.owner[hex] : 0;
    return owner !== 0 && owner !== v.me ? owner : 0;
  }

  /** A press on another nation's land (idle, playing): its menu opens if it is held still long enough. */
  private startHold(sx: number, sy: number): void {
    this.cancelHold();
    if (this.mode.type !== 'idle' || this.spectating) return;
    const owner = this.foreignAt(sx, sy);
    if (!owner) return;
    const timer = setTimeout(() => {
      this.hold = null;
      this.hooks.holdCue(null);
      if (!this.drag || this.drag.moved || this.hooks.modal()) return;
      // the press became the menu: its release does nothing
      this.drag = null;
      this.hooks.nationHold(owner, sx, sy);
    }, MENU_HOLD_MS);
    this.hold = { timer, owner };
    this.hooks.holdCue({ x: sx, y: sy, ms: MENU_HOLD_MS });
  }

  private cancelHold(): void {
    if (!this.hold) return;
    clearTimeout(this.hold.timer);
    this.hold = null;
    this.hooks.holdCue(null);
  }

  private move(e: PointerEvent): void {
    const [x, y] = this.local(e);
    const r = this.el.getBoundingClientRect();
    this.cursor = { sx: x, sy: y, inside: x >= 0 && y >= 0 && x <= r.width && y <= r.height };
    const p = this.pointers.get(e.pointerId);
    if (!p) return;
    if (this.pinch && this.pointers.size === 2) {
      p.x = x;
      p.y = y;
      const [a, b] = [...this.pointers.values()];
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      this.cam.zoomAt(d / Math.max(1, this.pinch.d), (a.x + b.x) / 2, (a.y + b.y) / 2, true);
      this.pinch.d = d;
      return;
    }
    const dx = x - p.x;
    const dy = y - p.y;
    p.x = x;
    p.y = y;
    const d = this.drag;
    if (!d) return;
    if (!d.moved && Math.hypot(x - d.x, y - d.y) > DRAG_PX) {
      d.moved = true;
      this.cancelHold();
    }
    if (d.moved) {
      this.cam.panBy(dx, dy);
      const now = performance.now();
      const dt = Math.max(1, now - d.lastT) / 1000;
      d.vx = 0.7 * d.vx + 0.3 * (dx / dt);
      d.vy = 0.7 * d.vy + 0.3 * (dy / dt);
      d.lastT = now;
    }
  }

  private up(e: PointerEvent): void {
    if (!this.pointers.has(e.pointerId)) return;
    this.pointers.delete(e.pointerId);
    this.cancelHold();
    if (this.pinch) {
      if (this.pointers.size < 2) this.pinch = null;
      this.drag = null;
      return;
    }
    const d = this.drag;
    this.drag = null;
    if (!d) return;
    if (d.moved) {
      if (performance.now() - d.lastT < 80) this.cam.fling(d.vx, d.vy);
      return;
    }
    const [x, y] = this.local(e);
    if (d.button === 2) this.rightClick(x, y, e.shiftKey);
    else if (d.button === 0) this.leftClick(x, y, e.shiftKey);
  }

  private cancelPointer(e: PointerEvent): void {
    this.pointers.delete(e.pointerId);
    this.cancelHold();
    this.drag = null;
    this.pinch = null;
  }

  private wheel(e: WheelEvent): void {
    e.preventDefault();
    if (this.hooks.modal()) return;
    const [x, y] = this.local(e);
    const delta = e.deltaMode === 1 ? e.deltaY * 30 : e.deltaY;
    this.cam.zoomAt(Math.pow(WHEEL_STEP, -delta), x, y, e.ctrlKey);
  }

  private leftClick(sx: number, sy: number, shift: boolean): void {
    const [wx, wy] = this.cam.screenToWorld(sx, sy);
    const view = this.store.view;
    if (this.mode.type === 'place') {
      const hex = this.placeHexAt(this.mode.kind, wx, wy);
      const err = checkPlacement(view, this.world, this.mode.kind, hex);
      if (err) {
        this.hooks.feedback('error', PLACE_ERR[err]);
        return;
      }
      this.hooks.send({ type: 'build', kind: this.mode.kind, hex });
      this.hooks.feedback('place');
      this.acted();
      if (!shift) this.setMode({ type: 'idle' });
      return;
    }
    if (this.mode.type === 'aim') {
      // a refused or warning click keeps aiming; a fired one ends it (Shift keeps aiming)
      if (this.aimAt(sx, sy, wx, wy, shift) && !shift) this.setMode({ type: 'idle' });
      return;
    }
    if (this.spectating) return;
    if (this.prioritize(sx, sy)) return;
    const hit = this.pick(sx, sy);
    if (hit) {
      const now = performance.now();
      const again = this.lastPick?.id === hit.id && now - this.lastPick.atMs < DOUBLE_CLICK_MS;
      this.lastPick = { id: hit.id, atMs: now };
      if (again && (isShipKind(hit.kind) || hit.kind === 'silo')) this.selected = this.ownOnScreen(hit.kind);
      else this.selected = shift ? (this.selected.includes(hit.id) ? this.selected.filter((i) => i !== hit.id) : [...this.selected, hit.id]) : [hit.id];
      this.hooks.feedback('order');
      return;
    }
    this.selected = [];
  }

  /** Click in aim mode; true if orders were sent. */
  private aimAt(sx: number, sy: number, wx: number, wy: number, shift: boolean): boolean {
    if (this.mode.type !== 'aim') return false;
    const a = this.mode.action;
    const sel = this.selection();
    if (a === 'launch') {
      const ripple = this.mode.ripple === true;
      const aim = computeAim(this.store.view, this.world, sel, wx, wy, false, ripple);
      if (!aim) return false;
      const act = aimClick(aim, shift, this.armedAt(sx, sy));
      if (act === 'refuse') {
        this.hooks.feedback('error', aim.blocked ? 'CANNOT TARGET AN ALLY' : (aim.reason ?? 'NO MISSILE READY'));
        return false;
      }
      if (act === 'warn') {
        this.ffArm = { sx, sy, untilMs: performance.now() + FF_CONFIRM_MS };
        this.hooks.feedback('warn', 'OWN TERRITORY · CLICK AGAIN TO FIRE');
        return false;
      }
      this.ffArm = null;
      const v = this.store.view;
      const reach = (e: EntityView) => e.kind !== 'sub' || inSlbmRange(e, v.tick, wx, wy);
      if (ripple) {
        // every loaded launcher that reaches fires now (if ready) and again as each reloads
        const ls = sel.filter((e) => e.owner === v.me && (e.kind === 'silo' || e.kind === 'sub') && e.state !== 'building' && loadedOf(e) > 0 && reach(e));
        for (const e of ls) this.ripples.set(e.id, { x: wx, y: wy, sentLoaded: null, sentMs: 0 });
        this.updateRipples();
        this.hooks.feedback('order', launchConfirm(aim.salvo, this.store.placeName(wx, wy), firstImpact(ls, v.tick, wx, wy), true, this.aimOwner(aim)));
        this.acted();
        return ls.length > 0;
      }
      const fire = readyLaunchers(v, sel).filter(reach);
      for (const e of fire) {
        // a launcher given a target by hand stops rippling at its old one
        this.ripples.delete(e.id);
        this.hooks.send({ type: 'launch', launcherId: e.id, x: wx, y: wy });
      }
      if (fire.length) {
        this.hooks.feedback('order', launchConfirm(aim.salvo, this.store.placeName(wx, wy), firstImpact(fire, v.tick, wx, wy), false, this.aimOwner(aim)));
        this.acted();
      }
      return fire.length > 0;
    } else {
      const order = airOrder(this.store.view, this.world, sel, a, wx, wy);
      if ('refuse' in order) {
        this.hooks.feedback('error', order.refuse);
        return false;
      }
      order.intents.forEach((i) => this.hooks.send(i));
      this.hooks.feedback('order', order.confirm);
      this.acted();
      return true;
    }
  }

  /** Selected SAMs + a click on a hostile missile head: engage that one first (§1.15). */
  private prioritize(sx: number, sy: number): boolean {
    const sams = this.selection().filter((e) => e.kind === 'sam' && e.owner === this.store.view.me && e.state !== 'building');
    const heads = this.lastScene?.heads;
    if (!sams.length || !heads) return false;
    let best: EntityId | null = null;
    let bd = MISSILE_PICK_PX * MISSILE_PICK_PX;
    for (const [id, p] of heads) {
      const [px, py] = this.cam.worldToScreen(p.x, p.y);
      const d = (px - sx) ** 2 + (py - sy) ** 2;
      if (d < bd) {
        bd = d;
        best = id;
      }
    }
    if (best === null) return false;
    for (const s of sams) this.hooks.send({ type: 'samPriority', samId: s.id, targetId: best });
    this.hooks.feedback('order');
    this.acted();
    return true;
  }

  /** Selected SAMs with a priority target: clear it (Esc's first layer). */
  private clearPriorities(): boolean {
    const set = this.selection().filter((e) => e.kind === 'sam' && e.owner === this.store.view.me && e.detail?.priorityId);
    for (const s of set) this.hooks.send({ type: 'samPriority', samId: s.id, targetId: null });
    if (set.length) this.hooks.feedback('order');
    return set.length > 0;
  }

  /** The rival whose land a launch is aimed at, by callsign (none for neutral land, the sea or our own). */
  private aimOwner(aim: AimState): string | undefined {
    return aim.zone === 'enemy' && aim.owner > 0 ? this.store.nameOf(aim.owner) : undefined;
  }

  private acted(): void {
    this.lastActMs = performance.now();
  }

  /** Our finished units of `kind` (ships, silos) drawn inside the view (double-click selection). */
  private ownOnScreen(kind: EntityView['kind']): EntityId[] {
    const v = this.store.view;
    const { w, h } = this.cam.vp;
    const own = [...v.entities.values()].filter((e) => e.kind === kind && e.owner === v.me && e.state !== 'building');
    return own
      .filter((e) => {
        const p = this.posOf(e);
        const [sx, sy] = this.cam.worldToScreen(p.x, p.y);
        return sx >= 0 && sy >= 0 && sx <= w && sy <= h;
      })
      .map((e) => e.id);
  }

  private rightClick(sx: number, sy: number, shift: boolean): void {
    if (this.spectating) return;
    if (this.mode.type !== 'idle') {
      this.setMode({ type: 'idle' });
      return;
    }
    const [wx, wy] = this.cam.screenToWorld(sx, sy);
    const hex = landHexAt(getGeo(this.world), wx, wy);
    const intents = rightClickIntents(this.store.view, this.selection(), wx, wy, hex, shift);
    // (a click back on the current focus cancels a held change: the last click wins)
    if (!intents.some((i) => i.type === 'focus') && hex >= 0 && hex === this.store.view.focus) this.heldFocus = null;
    intents.forEach((i) => (i.type === 'focus' ? this.sendFocus(i) : this.hooks.send(i)));
    if (intents.length) {
      if (intents[0].type === 'move') this.orderPing = { x: wx, y: wy, atMs: performance.now(), queue: shift };
      this.hooks.feedback('order');
      this.acted();
    }
  }

  // ---------------------------------------------------------------- keyboard

  private keydown(e: KeyboardEvent): void {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    const k = e.key.toLowerCase();
    if (k === 'escape') {
      this.escape();
      return;
    }
    if (this.hooks.modal()) {
      if (k === 'tab' || k === ' ') e.preventDefault();
      return;
    }
    if (k === 'tab') {
      e.preventDefault();
      this.hooks.toggleBoard(true);
      return;
    }
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    const n = Number(k);
    if (n >= 1 && n <= BUILD_ORDER.length) {
      this.startBuild(BUILD_ORDER[n - 1]);
      return;
    }
    if (['w', 'a', 's', 'd', 'arrowleft', 'arrowright', 'arrowup', 'arrowdown'].includes(k)) {
      this.keys.add(k);
      e.preventDefault();
      return;
    }
    if (k === 'h' || k === ' ') {
      e.preventDefault();
      this.hooks.hotkey('home');
      return;
    }
    if (k.length === 1) this.hooks.hotkey(k);
  }

  /**
   * Esc peels one layer: menu (close) → nation menu → mode → selection → (grace window after an
   * order: nothing) → open the menu.
   */
  escape(): void {
    if (this.hooks.modal()) {
      this.hooks.menu();
      return;
    }
    if (this.hooks.dismiss()) {
      this.acted();
      return;
    }
    if (this.mode.type !== 'idle') this.setMode({ type: 'idle' });
    else if (this.clearPriorities()) this.acted();
    else if (this.selected.length) this.selected = [];
    else if (performance.now() - this.lastActMs > ESC_GRACE_MS) this.hooks.menu();
  }

  private keyup(e: KeyboardEvent): void {
    const k = e.key.toLowerCase();
    if (k === 'tab') this.hooks.toggleBoard(false);
    this.keys.delete(k);
  }
}
